import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { useAuth } from '../store/authStore.js'
import { computeProfile } from '../lib/score.js'
import { DIMENSION_KEYS, DIMENSIONS } from '../surveys/dimensions.js'
import { ArrowLeft, Printer } from 'lucide-react'
import opLogo from '../assets/op.png'
import miwayLogo from '../assets/miway.png'

const TOC_ITEMS = [
  { id: 'einfuehrung',   label: 'Einführung' },
  { id: 'angaben',       label: 'Angaben zur Person' },
  { id: 'grafik',        label: 'Grafische Darstellung der Ergebnisse' },
  { id: 'top-motivator', label: 'Ihre wichtigste motivierende Wertedimension' },
  { id: 'top-demotivator', label: 'Ihre wichtigste demotivierende Wertedimension' },
  { id: 'liste-motiv',   label: 'Liste Ihrer Motivatoren' },
  { id: 'liste-demo',    label: 'Liste Ihrer Demotivatoren' },
]

const PERSON_FIELDS = [
  ['Vorname',                              (me) => me?.firstName],
  ['Nachname',                             (me) => me?.lastName],
  ['Höchster Bildungsabschluss',           (_, d) => d?.bildungsabschluss],
  ['Fachrichtung',                         (_, d) => d?.fachrichtung],
  ['Derzeitige Position',                  (_, d) => d?.position],
  ['Führungsverantwortung',                (_, d) => d?.fuehrungsverantwortung],
  ['Derzeitiger Bereich der Organisation', (_, d) => d?.bereich],
  ['Derzeitige Branche',                   (_, d) => d?.branche],
  ['Derzeitige Organisation',              (_, d) => d?.organisation],
]

export default function MiWayResult() {
  const { accessId } = useParams()
  const me = useAuth(s => s.user)
  const [data, setData] = useState(null)
  const [completedAt, setCompletedAt] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.getResponse(accessId).then(r => {
      setData(r?.data || {})
      setCompletedAt(r?.completedAt || null)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [accessId])

  if (loading) return <div className="text-ink-soft">Lade Ergebnis…</div>
  if (!data) return <div className="text-rose-700">Kein Ergebnis gefunden.</div>

  const profile = computeProfile(data)
  const topMotiv = profile.sortedMotiv[0]
  const topDemo = profile.sortedDemo[0]
  const demo = data.demographics || {}
  const dateLabel = formatDate(completedAt)

  return (
    <div className="space-y-8 max-w-5xl mx-auto print:space-y-0 print:max-w-none print:mx-0">
      {/* Action-Bar */}
      <div className="flex items-center justify-between print:hidden">
        <Link to="/" className="btn-ghost"><ArrowLeft size={18} /> Zurück zur Übersicht</Link>
        <button onClick={() => window.print()} className="btn-secondary"><Printer size={18} /> Drucken</button>
      </div>

      {/* Cover */}
      <header className="card overflow-hidden print:break-after-page">
        <div className="px-8 pt-8 pb-2 flex items-start justify-between gap-6">
          <div className="text-xs font-semibold tracking-widest text-teal-700 uppercase">WIR SEHEN. BUNT.</div>
          <img src={opLogo} alt="O+P Consult" className="h-10 w-auto" />
        </div>
        <div className="px-8 py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6 print:py-12">
          <div>
            <img src={miwayLogo} alt="miway" className="h-12 w-auto mb-4" />
            <div className="text-sm uppercase tracking-wider text-ink-soft">Ergebnisbericht · Version 4.0</div>
            <h1 className="text-3xl md:text-4xl font-bold mt-1 leading-tight">Werte- und<br />Motivationsanalyse</h1>
            <p className="text-lg text-ink mt-3">{[me?.firstName, me?.lastName].filter(Boolean).join(' ') || '—'}</p>
          </div>
          <div className="text-xs text-ink-soft md:text-right space-y-1">
            <p className="font-semibold text-ink">O+P Consult GmbH</p>
            <p>Hans-Thoma-Straße 98<br />68163 Mannheim</p>
            <p>
              <a href="mailto:bunt@op-consult.de" className="hover:underline">bunt@op-consult.de</a><br />
              <a href="https://www.op-consult.de" target="_blank" rel="noreferrer" className="hover:underline">www.op-consult.de</a>
            </p>
            {dateLabel && <p className="pt-2">Datum: {dateLabel}</p>}
            <p className="pt-3 text-[10px] text-ink-mute leading-snug">
              Der Prozess der berufsbezogenen Eignungsdiagnostik<br />
              ist nach DIN 33430 von DEKRA Assurance Services GmbH auditiert.
            </p>
          </div>
        </div>
        <div className="px-8 py-3 bg-teal-50/40 border-t border-teal-100/60 flex items-center justify-between text-xs text-ink-mute print:bg-transparent print:border-t-0 print:px-0 print:pt-8">
          <span>op-consult.de</span>
          <span>© O+P Consult GmbH</span>
          <span className="italic">Beratung, die bewegt.</span>
        </div>
      </header>

      {/* Inhaltsverzeichnis */}
      <section className="card p-8 print:break-before-page">
        <h2 className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-4">Inhaltsverzeichnis</h2>
        <ol className="space-y-2">
          {TOC_ITEMS.map((it, i) => (
            <li key={it.id} className="flex items-baseline gap-3">
              <span className="text-sm text-ink-mute w-5 shrink-0 text-right">{i + 1}</span>
              <a href={`#${it.id}`} className="text-ink hover:text-teal-700 hover:underline">{it.label}</a>
            </li>
          ))}
        </ol>
      </section>

      {/* 1. Einführung */}
      <Section number={1} id="einfuehrung" title="Einführung">
        <div className="space-y-3 text-ink leading-relaxed">
          <p>
            Im vorliegenden Ergebnisbericht finden Sie die Auswertung Ihrer durchgeführten
            MiWay-Analyse. Die Auswertung basiert auf den Motivatoren und Demotivatoren, die
            Sie im Arbeitskontext als für sich besonders wichtig markiert haben, sowie auf den
            jeweils zwei prioritären Definitionen, die Sie pro Begriff ausgewählt haben.
          </p>
          <p>
            Die Antworten werden einer von <strong>sieben Wertedimensionen</strong> zugeordnet, die
            unterschiedliche Werthaltungen im Arbeitsleben repräsentieren. Aus der Häufigkeit
            und Priorität Ihrer Auswahl ergibt sich Ihr persönliches Werteprofil.
          </p>
          <p>
            Der Bericht stellt die Grundlage für eine Selbstreflexion über Ihre wichtigsten
            beruflichen Werte dar und kann als Ausgangspunkt für Gespräche zu Karriereentwicklung,
            Teamzusammensetzung oder kultureller Passung dienen.
          </p>
        </div>
      </Section>

      {/* 2. Angaben zur Person */}
      <Section number={2} id="angaben" title="Angaben zur Person">
        <dl className="grid sm:grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-sm">
          {PERSON_FIELDS.map(([label, getter]) => (
            <FieldRow key={label} label={label} value={getter(me, demo)} />
          ))}
          {dateLabel && <FieldRow label="Datum" value={dateLabel} />}
        </dl>
      </Section>

      {/* 3. Grafische Darstellung */}
      <Section number={3} id="grafik" title="Grafische Darstellung der Ergebnisse">
        <p className="text-sm text-ink-soft mb-4">
          Ausprägung Ihrer Wertedimensionen — motivierende Anteile nach oben, demotivierende nach unten.
          Skalierung relativ zu Ihrem individuellen Höchstwert.
        </p>
        <BarChart profile={profile} />
        <Legend />
      </Section>

      {/* 4. Top motivator */}
      {topMotiv && (
        <Section number={4} id="top-motivator" title="Ihre wichtigste motivierende Wertedimension">
          <DimensionFullBlock dim={topMotiv} kind="motiv" />
        </Section>
      )}

      {/* 5. Top demotivator */}
      {topDemo && (
        <Section number={5} id="top-demotivator" title="Ihre wichtigste demotivierende Wertedimension">
          <DimensionFullBlock dim={topDemo} kind="demo" />
        </Section>
      )}

      {/* 6. Liste Motivatoren */}
      <Section number={6} id="liste-motiv" title="Liste Ihrer Motivatoren">
        <DefList kind="motiv" data={data} profile={profile} />
      </Section>

      {/* 7. Liste Demotivatoren */}
      <Section number={7} id="liste-demo" title="Liste Ihrer Demotivatoren">
        <DefList kind="demo" data={data} profile={profile} />
      </Section>
    </div>
  )
}

function Section({ number, id, title, children }) {
  return (
    <section
      id={id}
      className="card p-8 scroll-mt-24 print:break-before-page first:print:break-before-auto"
    >
      <div className="flex items-baseline gap-3 mb-5">
        <span className="text-2xl font-bold text-teal-700 leading-none">{number}</span>
        <h2 className="text-xl font-bold uppercase tracking-wide text-teal-800">{title}</h2>
      </div>
      {children}
    </section>
  )
}

function FieldRow({ label, value }) {
  return (
    <>
      <dt className="text-ink-soft">{label}:</dt>
      <dd className="text-ink">{value || '—'}</dd>
    </>
  )
}

function BarChart({ profile }) {
  const maxAbs = Math.max(
    ...DIMENSION_KEYS.map(k => Math.max(profile.dims[k].motivShare, profile.dims[k].demoShare)),
    0.01
  )
  const scale = 100 / maxAbs
  const barW = 56
  const gap = 26
  const innerH = 220
  const w = DIMENSION_KEYS.length * (barW + gap) + 80
  const h = innerH * 2 + 60

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" style={{ maxWidth: w }}>
        {[1, 0.75, 0.5, 0.25, 0, -0.25, -0.5, -0.75, -1].map(t => {
          const y = innerH + 30 - t * innerH
          return (
            <g key={t}>
              <line x1="60" x2={w - 10} y1={y} y2={y} stroke={t === 0 ? '#2D3E45' : '#E2EFF1'} strokeWidth={t === 0 ? 1.5 : 1} />
              <text x="50" y={y + 4} textAnchor="end" fontSize="11" fill="#5A6E76">
                {Math.round(t * 100)}%
              </text>
            </g>
          )
        })}
        {DIMENSION_KEYS.map((k, i) => {
          const dim = profile.dims[k]
          const motivH = dim.motivShare * scale * (innerH / 100)
          const demoH = dim.demoShare * scale * (innerH / 100)
          const x = 70 + i * (barW + gap)
          const baseY = innerH + 30
          return (
            <g key={k}>
              {motivH > 0 && (
                <rect x={x} y={baseY - motivH} width={barW} height={motivH} fill={dim.color} rx="2" />
              )}
              {demoH > 0 && (
                <rect x={x} y={baseY} width={barW} height={demoH} fill={dim.color} rx="2" opacity="0.85" />
              )}
            </g>
          )
        })}
        <text x="22" y={innerH / 2 + 12} fontSize="11" fill="#5A6E76" transform={`rotate(-90 22 ${innerH / 2 + 12})`}>
          motivierend
        </text>
        <text x="22" y={innerH * 1.5 + 50} fontSize="11" fill="#5A6E76" transform={`rotate(-90 22 ${innerH * 1.5 + 50})`}>
          demotivierend
        </text>
      </svg>
    </div>
  )
}

function Legend() {
  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
      {DIMENSION_KEYS.map(k => {
        const d = DIMENSIONS[k]
        return (
          <div key={k} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: d.color }} />
            <span className="text-ink-soft">{d.label}</span>
          </div>
        )
      })}
    </div>
  )
}

// Voller Block für Top-Motivator/Demotivator: alle 8 Spec-Inhalte plus Liste der ausgewählten Antworten.
function DimensionFullBlock({ dim, kind }) {
  const content = kind === 'motiv' ? dim.motivator : dim.demotivator
  if (!content) return null
  const entries = (kind === 'motiv' ? dim.motivDefs : dim.demoDefs).slice().sort((a, b) => a.prio - b.prio)
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="px-2.5 py-1 rounded text-white text-xs font-semibold" style={{ background: dim.color }}>
          {dim.short}
        </span>
        <h3 className="text-lg font-bold">{content.headline}</h3>
      </div>

      <Html className="prose-tight" content={content.intro} />

      {entries.length > 0 && (
        <div>
          <Html className="prose-tight" content={content.listIntro} />
          <ul className="space-y-1.5 list-disc pl-5 text-ink mt-2">
            {entries.map((e, i) => (
              <li key={i}>
                <strong>{e.word}</strong> {kind === 'motiv' ? 'motiviert' : 'demotiviert'} Sie, wenn{' '}
                {e.defText.replace(/\.$/, '')}.
                <span className="ml-2 text-xs text-ink-mute">(Prio {e.prio})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Html className="prose-tight" content={content.description} />
      <Html className="prose-tight" content={content.preferred} />
      <Html className="prose-tight" content={content.collaboration} />
      <Html className="prose-tight" content={content.leadership} />
      <Html className="prose-tight" content={content.problemSolving} />
      <Html className="prose-tight" content={content.environment} />
      <Html className="prose-tight" content={content.critical} />
    </div>
  )
}

// Kleine HTML-Komponente: rendert Spec-HTML (h4, p, em, ul, li, br) als sauberer Tailwind-Block.
function Html({ content, className = '' }) {
  if (!content) return null
  return (
    <div
      className={`miway-html ${className}`}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

function DefList({ kind, data, profile }) {
  const block = kind === 'motiv' ? data.motivators : data.demotivators
  const source = block?.selected || []
  const additional = (block?.additional || []).map(s => (s || '').trim()).filter(Boolean)
  const verb = kind === 'motiv' ? 'motiviert' : 'demotiviert'

  if (!source.length && !additional.length) return <p className="text-ink-soft">—</p>

  return (
    <div className="space-y-6">
      {source.map(word => {
        const allDefs = (kind === 'motiv'
          ? Object.values(profile.dims).flatMap(d => d.motivDefs)
          : Object.values(profile.dims).flatMap(d => d.demoDefs))
          .filter(e => e.word === word)
          .sort((a, b) => a.prio - b.prio)
        return (
          <div key={word}>
            <div className="font-semibold mb-1.5">{word} {verb} Sie, wenn</div>
            <ul className="space-y-1.5">
              {allDefs.map((entry, i) => {
                const dim = Object.values(profile.dims).find(d =>
                  (kind === 'motiv' ? d.motivDefs : d.demoDefs).includes(entry)
                )
                return (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="px-2 py-0.5 rounded text-white text-xs font-semibold shrink-0" style={{ background: dim?.color }}>
                      {dim?.short}
                    </span>
                    <span>{entry.defText}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}

      {additional.length > 0 && (
        <div className="border-t border-teal-100 pt-5">
          <div className="font-semibold mb-2">
            Weitere {kind === 'motiv' ? 'Motivatoren' : 'Demotivatoren'}, die Sie ergänzt haben:
          </div>
          <ul className="list-disc pl-5 space-y-1 text-ink">
            {additional.map((txt, i) => <li key={i}>{txt}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function formatDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
